import { defineStore } from "pinia";
import {
    BankDailyBalancePage,
    BankDailyBalanceRecord,
    BankDailyBalanceRequest, BankTransactionDailyBalancePage, FetchState,
    FetchStateKey,
    HsabBalanceState,
    HsaDailyBalance
} from "@/shared/types";
import _ from "lodash";
import moment from "moment/moment";
import * as bankService from "@/shared/api/bankService";
import { errorIsStatus } from "@/shared/errorHelper";
import * as storeHelper from "@/shared/storeHelper";
import { fetchStateKeyMatch } from "@/shared/fetchStateHelper";
import Vue from "vue";
import { useHsaDailyBalanceStore } from "@/shared/store/hsaDailyBalance";

const currentDateFormat = "DD-MMM-YY";

const initialState: HsabBalanceState = {
    fetchState: FetchState.Uninitialized,
    fetchKey: null,
    dailyBalances: [],
    dailyBalancesDictionary: {},
    currentEndDateKey: null,
};
export const useHsabDailyBalanceStore = defineStore("hsabDailyBalance", {
    state: (): HsabBalanceState => (_.cloneDeep(initialState)),
    getters: {
        currentDateFormat () {
            return currentDateFormat;
        },
       balancesForCurrentDates () {
            const unmodifiedDailyBalances: HsaDailyBalance[] = (
                (this.dailyBalancesDictionary && this.dailyBalancesDictionary[this.currentEndDateKey])
                    ? this.dailyBalancesDictionary[this.currentEndDateKey]
                    : null
            );

            const hsaDailyBalanceStore = useHsaDailyBalanceStore();

            const pendingAmount = hsaDailyBalanceStore.getInstantBalance.amount && (this.pendingAmount || 0);
            if (unmodifiedDailyBalances && pendingAmount) {
                // incorporate the webservice "instant balance" into the graph data
                // by either updating the current days balance or appending a new point.

                // Expand the date range to attempt to account for west coast users late in the day or early in the morning
                const impact = moment().add(-9, "hours").startOf("day").unix();

                if (unmodifiedDailyBalances.some(v => v.timestamp > impact)) {
                    return unmodifiedDailyBalances.map(v => {
                        return {
                            ...v,
                            balance: (v.timestamp > impact) ? hsaDailyBalanceStore.getInstantBalance.amount : v.balance
                        };
                    });
                }

                // it's possible the user clicked "page back" and the data set
                // under investigation is for a prior period of time which should not
                // be impacted by the current webservice balance.
                if (moment(this.currentEndDateKey, "YYYYMMDD").unix() >= impact) {
                    return [ ...unmodifiedDailyBalances, {
                        balance: hsaDailyBalanceStore.getInstantBalance.amount,
                        date: moment().format(currentDateFormat)
                    } ];
                }
            }

            return unmodifiedDailyBalances;
        }
    },
    actions: {
        setCurrentEndDateKey (currentEndDateKey: string): void {
            this.currentEndDateKey = currentEndDateKey;
        },
        setStateLoading (key: FetchStateKey): void {
            this.fetchState = FetchState.Loading;
            this.fetchKey = key;
        },
        setDailyBalancesState (
            mutationArguments: {
                stateKey: FetchStateKey, balanceData: BankTransactionDailyBalancePage
            }): void {

            if (this.fetchState !== FetchState.Loading ||
                !fetchStateKeyMatch(this.fetchKey, mutationArguments.stateKey)) {
                return;
            }
            const data = mutationArguments.balanceData;
            const records = _.sortBy(data.records.map(x => {
                const momentInst = moment(x.date);
                return {
                    ...x,
                    timestamp: momentInst.unix(),
                    date: momentInst.format(currentDateFormat)
                };
            }), [ "timestamp" ]);

            this.fetchState = FetchState.Loaded;
            this.dailyBalances = [ ...this.dailyBalances, ...records ];
            Vue.set(this.dailyBalancesDictionary, `${this.currentEndDateKey}`, records);
            this.periodStartBalance = data.periodStartBalance;
            this.accountStartDate = data.accountStartDate;
        },
        handleNoDailyBalanceRecords (params: {
            stateKey: FetchStateKey, firstRecordDate: string,
            accountStartDate: string, periodStartBalance?: number
        }): void {
            const currentDateString = moment().format("YYYY-MM-DD");
            const zeroBalanceData: BankDailyBalancePage = {
                 records: [ {
                    balance:  0,
                    date: currentDateString,
                    timestamp: moment(currentDateString).unix()
                } ]
            };

         //   this.setHsaAccountStatus({ stateKey: params.stateKey, accountStatus: false });
            this.setDailyBalancesState({ stateKey: params.stateKey, balanceData: zeroBalanceData });
        },
        invalidateBalanceFetchState (key: FetchStateKey) {
            if (fetchStateKeyMatch(key, this.fetchKey)) {
                return;
            }
            this.dailyBalances = [];
            this.dailyBalancesDictionary = {};
            this.balanceOnFile = {
                fetchState: FetchState.Uninitialized,
                asOfDate: null,
                amount: 0
            };
            this.fetchState = FetchState.Uninitialized;
        },

        loadHsabHsaDailyBalances (dateParams: BankDailyBalanceRequest, force: boolean): Promise<void> {
            const fetchStateKey: FetchStateKey = {
                username: dateParams.username,
                partnerId: dateParams.partnerId
            };

            this.invalidateBalanceFetchState(fetchStateKey);

            // already loading ANYTHING then ditch out
            if (this.fetchState === FetchState.Loading) {
                return Promise.resolve();
            }

            // assume page in dictionary is a loaded page
            if (this.dailyBalancesDictionary &&
                this.dailyBalancesDictionary[this.currentEndDateKey] && !force) {
                return Promise.resolve();
            }

            this.setStateLoading(fetchStateKey);

            return bankService.getHsabHsaDailyBalances(dateParams.startDate, dateParams.endDate, dateParams.username,
                    dateParams.partnerId)
                .then(response => {
                    const records: BankDailyBalanceRecord[] = _.get(response, "data.records", []);

                    const currentDate = moment();
                    const paramEndDate = moment(dateParams.endDate);
                    const useParamEndDate = paramEndDate.isBefore(currentDate);

                    if (records.length === 0) {
                        this.handleNoDailyBalanceRecords({
                            fetchStateKey,
                            firstRecordDate: useParamEndDate ? paramEndDate.format("YYYY-MM-DD") : null,
                        });
                    } else {
                        this.setDailyBalancesState({ stateKey: fetchStateKey, balanceData: response.data });
                    }
                })
                .catch(err => {
                    if (errorIsStatus(err, 404)) {
                        this.handleNoDailyBalanceRecords({ fetchStateKey });
                        return;
                    }
                    storeHelper.handleError({ error: err, rethrow: false });
                });
        },
    }
});